import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Send, LoaderCircle } from "lucide-react";
import axios from "axios";
import { useLang } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const reveal = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
};

const inputCls =
  "w-full rounded-sm bg-[#110E0C] border border-[#2D2520] px-4 py-3.5 text-sm text-[#F9F6F0] placeholder:text-[#A69C95]/50 focus:ring-1 focus:ring-[#D48C46] focus:border-[#D48C46] transition-colors duration-300";

export default function Contact() {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", restaurant: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t.contact.success);
      setForm({ name: "", restaurant: "", email: "", phone: "", message: "" });
    } catch {
      toast.error(t.contact.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="kontakt"
      className="py-24 md:py-32 border-t border-[#2D2520]"
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
        <motion.div {...reveal} transition={{ duration: 0.8 }}>
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#D48C46] mb-5">
            {t.contact.label}
          </p>
          <h2 className="font-serif font-medium text-4xl sm:text-5xl tracking-tight leading-tight text-[#F9F6F0]">
            {t.contact.title}
          </h2>
          <p className="mt-6 text-base md:text-lg leading-relaxed text-[#A69C95] max-w-md">
            {t.contact.body}
          </p>
        </motion.div>

        <motion.form
          {...reveal}
          transition={{ duration: 0.8, delay: 0.15 }}
          onSubmit={submit}
          className="rounded-xl border border-[#2D2520] bg-[#1A1512] p-8 md:p-10 space-y-6"
          data-testid="contact-form"
        >
          <div>
            <label htmlFor="contact-name" className="block text-xs uppercase tracking-[0.2em] font-semibold text-[#A69C95] mb-2">
              {t.contact.fields.name}
            </label>
            <input
              id="contact-name"
              data-testid="contact-name-input"
              required
              value={form.name}
              onChange={set("name")}
              placeholder={t.contact.fields.namePlaceholder}
              className={inputCls}
            />
          </div>
          <div>
            <label htmlFor="contact-restaurant" className="block text-xs uppercase tracking-[0.2em] font-semibold text-[#A69C95] mb-2">
              {t.contact.fields.restaurant}
            </label>
            <input
              id="contact-restaurant"
              data-testid="contact-restaurant-input"
              required
              value={form.restaurant}
              onChange={set("restaurant")}
              placeholder={t.contact.fields.restaurantPlaceholder}
              className={inputCls}
            />
          </div>
          <div>
            <label htmlFor="contact-email" className="block text-xs uppercase tracking-[0.2em] font-semibold text-[#A69C95] mb-2">
              {t.contact.fields.email}
            </label>
            <input
              id="contact-email"
              data-testid="contact-email-input"
              type="email"
              required
              value={form.email}
              onChange={set("email")}
              placeholder={t.contact.fields.emailPlaceholder}
              className={inputCls}
            />
          </div>
          <div>
            <label htmlFor="contact-phone" className="block text-xs uppercase tracking-[0.2em] font-semibold text-[#A69C95] mb-2">
              {t.contact.fields.phone}
            </label>
            <input
              id="contact-phone"
              data-testid="contact-phone-input"
              type="tel"
              required
              value={form.phone}
              onChange={set("phone")}
              placeholder={t.contact.fields.phonePlaceholder}
              className={inputCls}
            />
          </div>
          <div>
            <label htmlFor="contact-message" className="block text-xs uppercase tracking-[0.2em] font-semibold text-[#A69C95] mb-2">
              {t.contact.fields.message}
            </label>
            <textarea
              id="contact-message"
              data-testid="contact-message-input"
              required
              rows={5}
              value={form.message}
              onChange={set("message")}
              placeholder={t.contact.fields.messagePlaceholder}
              className={`${inputCls} resize-none`}
            />
          </div>
          <button
            type="submit"
            disabled={sending}
            data-testid="contact-submit-button"
            className="w-full inline-flex items-center justify-center gap-2 rounded-sm bg-[#D48C46] px-8 py-4 text-sm font-semibold text-[#110E0C] hover:bg-[#F0A862] disabled:opacity-60 transition-colors duration-300"
          >
            {sending ? (
              <>
                <LoaderCircle size={16} className="animate-spin" />
                {t.contact.sending}
              </>
            ) : (
              <>
                <Send size={16} />
                {t.contact.submit}
              </>
            )}
          </button>
        </motion.form>

        <motion.div
          {...reveal}
          transition={{ duration: 0.8, delay: 0.25 }}
          className="lg:col-start-2 -mt-4 grid grid-cols-1 sm:grid-cols-2 gap-8 text-sm text-[#A69C95]"
          data-testid="contact-info-block"
        >
          <div className="space-y-2">
            <p className="text-[#F9F6F0] font-semibold">{t.contact.infoEmail}</p>
            <p>
              <a
                href="mailto:support@bordverket.com"
                className="hover:text-[#D48C46] transition-colors duration-300"
                data-testid="contact-email-support"
              >
                support@bordverket.com
              </a>
            </p>
          </div>
          <div className="space-y-2">
            <p className="text-[#F9F6F0] font-semibold">{t.contact.infoPhone}</p>
            <p>
              <a
                href="tel:+4798340354"
                className="hover:text-[#D48C46] transition-colors duration-300"
                data-testid="contact-phone-daniel"
              >
                983 40 354
              </a>
            </p>
            <p>
              <a
                href="tel:+4797694791"
                className="hover:text-[#D48C46] transition-colors duration-300"
                data-testid="contact-phone-adrian"
              >
                976 94 791
              </a>
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
